import { US_STATES } from '../constants/states';
import type {
	CreateLeadActionConfigModel,
	NotifyActionConfigModel,
} from './form-actions';
import type {
	FormAnswerValue,
	FormFieldDefinitionModel,
	FormFieldType,
	FormFieldValidationRulesModel,
} from './form-types';

/**
 * PD-16574 — the storefront registration form as a Workd form
 * (docs/claude/forms/02-registration-migration.md). Pure, so the data migration, the stock
 * "Registration" form and the tests build the very same thing.
 *
 * The fields are today's Register page (RegistrationForm.tsx) in its order and wording:
 * company name, first and last name, email, phone, the address, business type, "How did
 * you hear about us?", the brand's own questions, then "Anything else we should know?".
 * Field keys stay exactly as today (`contactFirstName`, `companyName`, …), so answers
 * already stored under them keep their meaning and the old /api/portal/register body maps
 * straight onto them. First name, last name, email and phone are required, as today, but
 * no longer locked.
 */

export const REGISTRATION_FORM_KEY = 'registration';

/** A standard field's resolved setting (resolveStandardFieldConfig). */
export interface RegistrationStandardSettingModel {
	fieldKey: string;
	isVisible: boolean;
	isRequired: boolean;
}

/** A brand's own registration question (registration_field). */
export interface RegistrationQuestionModel {
	fieldKey: string;
	label: string;
	fieldType: string;
	placeholder: string | null;
	helpText: string | null;
	isRequired: boolean;
	defaultValue: string | null;
	selectOptions: Array<{ value: string; label: string }> | null;
	validationRules: Record<string, unknown> | null;
	includeInEmail: boolean;
}

type FieldSeed = Pick<FormFieldDefinitionModel, 'fieldKey' | 'label'> &
	Partial<FormFieldDefinitionModel>;

/** The standard fields as the Register page shows them; `standard` fields can be hidden. */
const PAGE_FIELDS: ReadonlyArray<FieldSeed & { standard: boolean }> = [
	{
		fieldKey: 'companyName',
		label: 'Company Name',
		identity: 'company',
		standard: true,
	},
	{
		fieldKey: 'contactFirstName',
		label: 'First Name',
		identity: 'first_name',
		standard: false,
	},
	{
		fieldKey: 'contactLastName',
		label: 'Last Name',
		identity: 'last_name',
		standard: false,
	},
	{
		fieldKey: 'email',
		label: 'Email',
		fieldType: 'email',
		identity: 'email',
		standard: false,
	},
	{
		fieldKey: 'phone',
		label: 'Phone',
		fieldType: 'phone',
		identity: 'phone',
		standard: false,
	},
	{ fieldKey: 'address1', label: 'Street Address', standard: true },
	{ fieldKey: 'address2', label: 'Address Line 2', standard: true },
	{ fieldKey: 'city', label: 'City', standard: true },
	{
		fieldKey: 'state',
		label: 'State',
		fieldType: 'select',
		placeholder: 'Select State',
		selectOptions: US_STATES.map((usState) => ({
			value: usState.value,
			label: usState.label,
		})),
		standard: true,
	},
	{ fieldKey: 'zip', label: 'ZIP', standard: true },
	{
		fieldKey: 'businessType',
		label: 'Business Type',
		placeholder: 'Restaurant, grocery, distributor, …',
		standard: true,
	},
	{
		fieldKey: 'leadSource',
		label: 'How did you hear about us?',
		placeholder: 'Trade show, referral, search, …',
		standard: true,
	},
];

/** Last on the page, after the brand's own questions. */
const NOTES_FIELD: FieldSeed = {
	fieldKey: 'notes',
	label: 'Anything else we should know?',
	fieldType: 'textarea',
	identity: 'message',
};

const QUESTION_TYPES: ReadonlySet<string> = new Set<FormFieldType>([
	'text',
	'textarea',
	'number',
	'currency',
	'date',
	'datetime',
	'boolean',
	'select',
	'multiselect',
	'phone',
	'email',
	'url',
]);

function definition(
	seed: FieldSeed,
	sortOrder: number,
): FormFieldDefinitionModel {
	return {
		adminLabel: null,
		fieldType: 'text',
		placeholder: null,
		helpText: null,
		isRequired: false,
		defaultValue: null,
		selectOptions: null,
		validationRules: null,
		identity: null,
		consentPurpose: null,
		policyVersions: null,
		termsBody: null,
		termsUrl: null,
		termsLinkText: null,
		includeInNotification: true,
		...seed,
		sortOrder,
	};
}

/** Only the named limits a form field understands; anything else was never checked. */
function knownRules(
	rules: Record<string, unknown> | null,
): FormFieldValidationRulesModel | null {
	if (rules == null) {
		return null;
	}
	const kept: Record<string, unknown> = {};
	for (const key of ['minLength', 'maxLength', 'pattern', 'min', 'max']) {
		const value = rules[key];
		if (typeof value === 'number' || typeof value === 'string') {
			kept[key] = value;
		}
	}
	return Object.keys(kept).length > 0
		? (kept as FormFieldValidationRulesModel)
		: null;
}

/**
 * The registration form's fields for one brand: the standard fields it shows (with its
 * required settings), the four contact fields, its own questions, then notes if shown.
 * `standard` is keyed by field key; a key it lacks keeps the page default (shown).
 */
export function registrationFormFields(
	standard: ReadonlyArray<RegistrationStandardSettingModel>,
	questions: ReadonlyArray<RegistrationQuestionModel>,
): Array<FormFieldDefinitionModel> {
	const settings = new Map(
		standard.map((setting) => [setting.fieldKey, setting]),
	);
	const shown = (fieldKey: string): RegistrationStandardSettingModel | null => {
		const setting = settings.get(fieldKey);
		if (setting == null) {
			return { fieldKey, isVisible: true, isRequired: false };
		}
		return setting.isVisible ? setting : null;
	};

	const seeds: Array<FieldSeed> = [];
	for (const { standard: isStandard, ...seed } of PAGE_FIELDS) {
		if (!isStandard) {
			seeds.push({ ...seed, isRequired: true });
			continue;
		}
		const setting = shown(seed.fieldKey);
		if (setting != null) {
			seeds.push({ ...seed, isRequired: setting.isRequired });
		}
	}
	for (const question of questions) {
		seeds.push({
			fieldKey: question.fieldKey,
			label: question.label,
			fieldType: QUESTION_TYPES.has(question.fieldType)
				? (question.fieldType as FormFieldType)
				: 'text',
			placeholder: question.placeholder,
			helpText: question.helpText,
			isRequired: question.isRequired,
			defaultValue: question.defaultValue,
			selectOptions:
				question.selectOptions?.map((option) => ({ ...option })) ?? null,
			validationRules: knownRules(question.validationRules),
			includeInNotification: question.includeInEmail,
		});
	}
	const notes = shown(NOTES_FIELD.fieldKey);
	if (notes != null) {
		seeds.push({ ...NOTES_FIELD, isRequired: notes.isRequired });
	}
	return seeds.map((seed, index) => definition(seed, index));
}

/**
 * Where each registration answer goes on the lead, as the old registration wrote it: the
 * company, contact, email, phone and address onto the lead and its contact, notes into
 * the lead's note. The brand's own questions are left unmapped, so they join the note,
 * as the old registration email listed them.
 */
export function registrationLeadMapping(
	fields: ReadonlyArray<Pick<FormFieldDefinitionModel, 'fieldKey'>>,
): Record<string, string> {
	const targets: Record<string, string> = {
		companyName: 'client.name',
		contactFirstName: 'contact.firstName',
		contactLastName: 'contact.lastName',
		email: 'client.email',
		phone: 'client.phone',
		address1: 'client.address1',
		address2: 'client.address2',
		city: 'client.city',
		state: 'client.state',
		zip: 'client.zip',
		businessType: 'client.businessType',
		leadSource: 'client.leadSource',
		notes: 'note',
	};
	const mapping: Record<string, string> = {};
	for (const field of fields) {
		const target = targets[field.fieldKey];
		if (target != null) {
			mapping[`field:${field.fieldKey}`] = target;
		}
	}
	return mapping;
}

/**
 * The registration form's create-lead settings: lead source only from the answer, and no
 * rep unless one is chosen later (owner, 2026-09-28).
 *
 * PD-17583 — a registration that confidently matches a client already on file makes no
 * lead (`skip`): the submission stays New in the Forms inbox, marked as already in Workd,
 * for a person to review. It used to be `create` (a new lead with the match recorded as its
 * potential duplicate, as the old registration did), which gave Motor City Ice a duplicate
 * client when a customer staff had just set up also filled in the Register page.
 * Already-saved forms are moved over by 1793154783385-SkipLeadForMatchedRegistrations.
 */
export function registrationLeadConfig(
	fields: ReadonlyArray<Pick<FormFieldDefinitionModel, 'fieldKey'>>,
): CreateLeadActionConfigModel {
	return {
		mapping: registrationLeadMapping(fields),
		leadSource: null,
		assignRep: { mode: 'none', userId: null },
		whenMatched: 'skip',
	};
}

/**
 * Who today's registration notifies, as a notify action: the addresses of active users
 * become those users (email and bell, as today), other addresses get the email.
 */
export function registrationNotifyConfig(
	userIds: ReadonlyArray<number>,
	emailAddresses: ReadonlyArray<string>,
): NotifyActionConfigModel {
	return {
		recipients: {
			roleIds: [],
			userIds: [...userIds],
			emailAddresses: [...emailAddresses],
		},
		matchedClientRep: false,
		assignee: false,
		channels: { email: true, bell: true },
		subject: 'New {{formName}} from {{submitterName}}',
	};
}

/** The addresses in a recipient setting: one address or a comma-separated list. */
export function splitRecipientAddresses(value: string | null): Array<string> {
	if (value == null) {
		return [];
	}
	return [
		...new Set(
			value
				.split(',')
				.map((address) => address.trim())
				.filter((address) => address.length > 0),
		),
	];
}

/** The old /api/portal/register body (and a stored old registration), as field keys. */
export interface LegacyRegistrationModel {
	name?: string | null;
	contactFirstName?: string | null;
	contactLastName?: string | null;
	email?: string | null;
	phone?: string | null;
	address1?: string | null;
	address2?: string | null;
	city?: string | null;
	state?: string | null;
	zip?: string | null;
	businessType?: string | null;
	leadSource?: string | null;
	notes?: string | null;
	registrationFields?: Record<string, unknown> | null;
}

/**
 * The answers of an old registration keyed by the form's field keys. The company name
 * travelled as `name`; everything else already had its field key. Empty values are left
 * out, and a brand question's answer keeps its stored value.
 */
export function legacyRegistrationAnswers(
	legacy: LegacyRegistrationModel,
): Record<string, unknown> {
	const answers: Record<string, unknown> = {};
	const text: Array<[string, string | null | undefined]> = [
		['companyName', legacy.name],
		['contactFirstName', legacy.contactFirstName],
		['contactLastName', legacy.contactLastName],
		['email', legacy.email],
		['phone', legacy.phone],
		['address1', legacy.address1],
		['address2', legacy.address2],
		['city', legacy.city],
		['state', legacy.state],
		['zip', legacy.zip],
		['businessType', legacy.businessType],
		['leadSource', legacy.leadSource],
		['notes', legacy.notes],
	];
	for (const [key, value] of text) {
		if (typeof value === 'string' && value.trim() !== '') {
			answers[key] = value.trim();
		}
	}
	for (const [key, value] of Object.entries(legacy.registrationFields ?? {})) {
		if (key in answers || value == null || value === '') {
			continue;
		}
		answers[key] = value;
	}
	return answers;
}

/** The old wire name of a field key, for errors sent back to an old page (`name`). */
export function legacyRegistrationErrorKey(fieldKey: string): string {
	return fieldKey === 'companyName' ? 'name' : fieldKey;
}

/**
 * A first and last name from a stored old registration, which kept only the full name
 * (`ownerName`): the first word, and the rest.
 */
export function splitOwnerName(ownerName: string | null): {
	first: string | null;
	last: string | null;
} {
	const parts = (ownerName ?? '')
		.trim()
		.split(/\s+/)
		.filter((part) => part !== '');
	if (parts.length === 0) {
		return { first: null, last: null };
	}
	return {
		first: parts[0] ?? null,
		last: parts.length > 1 ? parts.slice(1).join(' ') : null,
	};
}

/** Answers keep their stored type; anything else becomes text. */
export function asFormAnswer(value: unknown): FormAnswerValue | null {
	if (
		typeof value === 'string' ||
		typeof value === 'number' ||
		typeof value === 'boolean'
	) {
		return value;
	}
	if (Array.isArray(value)) {
		return value.map((item) => String(item));
	}
	return value == null ? null : JSON.stringify(value);
}

/**
 * The questions a past registration was made against, from its stored `fieldSnapshot`
 * (key, label, type and choices; the rest was not kept).
 */
export function questionsFromSnapshot(
	snapshot: ReadonlyArray<Record<string, unknown>> | null,
): Array<RegistrationQuestionModel> {
	return (snapshot ?? [])
		.filter(
			(entry) =>
				typeof entry['fieldKey'] === 'string' && entry['fieldKey'] !== '',
		)
		.map((entry) => ({
			fieldKey: String(entry['fieldKey']),
			label:
				typeof entry['label'] === 'string'
					? entry['label']
					: String(entry['fieldKey']),
			fieldType:
				typeof entry['fieldType'] === 'string' ? entry['fieldType'] : 'text',
			placeholder: null,
			helpText: null,
			isRequired: false,
			defaultValue: null,
			selectOptions: Array.isArray(entry['selectOptions'])
				? (entry['selectOptions'] as Array<{ value: string; label: string }>)
				: null,
			validationRules: null,
			includeInEmail: true,
		}));
}

/** A stored old registration (client_registration_new) as the copy reads it. */
export interface PastRegistrationModel {
	name: string | null;
	ownerName: string | null;
	email: string | null;
	phone: string | null;
	address1: string | null;
	address2: string | null;
	city: string | null;
	state: string | null;
	zip: string | null;
	registrationFields: Record<string, unknown> | null;
}

/** A past registration's answers under the form's field keys. */
export function pastRegistrationAnswers(
	past: PastRegistrationModel,
): Record<string, FormAnswerValue> {
	const { first, last } = splitOwnerName(past.ownerName);
	const answers: Record<string, FormAnswerValue> = {};
	for (const [key, value] of Object.entries(
		legacyRegistrationAnswers({
			name: past.name,
			contactFirstName: first,
			contactLastName: last,
			email: past.email,
			phone: past.phone,
			address1: past.address1,
			address2: past.address2,
			city: past.city,
			state: past.state,
			zip: past.zip,
			registrationFields: past.registrationFields,
		}),
	)) {
		const answer = asFormAnswer(value);
		if (answer != null) {
			answers[key] = answer;
		}
	}
	return answers;
}
